// ==================== SUB-ACCOUNTS MANAGEMENT PAGE ====================
// The main customer creates and manages sub-account logins.

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UsersRound,
  Plus,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  Search,
  UserCheck,
  UserX,
  Clock,
} from 'lucide-react';
import { Button, Card, Input, Modal, Select } from '../../../components/ui';
import type { SelectOption } from '../../../components/ui/Select';
import {
  SubAccountItem,
  SubAccountForm,
  SubAccountConfirmDialogs,
  SubAccountContactsModal,
  type SubAccountFormMode,
} from '../../../components/sub-accounts';
import { useOrganizations } from '../../../hooks/useOrganizations';
import { useSubAccounts } from '../../../hooks/useSubAccounts';
import { subAccountsService, type SubAccount } from '../../../services/sub-accounts.service';

// ==================== FILTER OPTIONS ====================

const FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All Sub-accounts' },
  { value: 'active', label: 'Active Only' },
  { value: 'inactive', label: 'Inactive Only' },
];

const FORM_TITLES: Record<SubAccountFormMode, { title: string; subtitle: string }> = {
  create: { title: 'Add Sub-account', subtitle: 'Give a team member access to assigned contacts and WhatsApp chat' },
  edit: { title: 'Edit Sub-account', subtitle: 'Update sub-account details' },
  password: { title: 'Set New Password', subtitle: 'The sub-account will be signed out everywhere' },
};

// ==================== MAIN COMPONENT ====================

const SubAccountsPage = () => {
  const { organizations, isMultiOrganization, selectedOrganizationId } = useOrganizations();
  const {
    subAccounts,
    loading,
    error,
    setError,
    success,
    setSuccess,
    showSuccess,
    fetchSubAccounts,
    deleteConfirm,
    setDeleteConfirm,
    deleting,
    handleDelete,
    toggleConfirm,
    setToggleConfirm,
    toggling,
    confirmToggleStatus,
  } = useSubAccounts(subAccountsService);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Modal state
  const [formMode, setFormMode] = useState<SubAccountFormMode | null>(null);
  const [selectedSubAccount, setSelectedSubAccount] = useState<SubAccount | null>(null);

  // Assigned contacts modal
  const [contactsSubAccount, setContactsSubAccount] = useState<SubAccount | null>(null);

  // ==================== FILTERED SUB-ACCOUNTS ====================

  // Multiple organizations: the account chosen in the header scopes the list and the counts
  // (all accounts when none). With one organization nothing is selected and every sub-account shows.
  const accountSubAccounts = useMemo(
    () =>
      selectedOrganizationId
        ? subAccounts.filter((subAccount) => subAccount.assignedOrganizationId === selectedOrganizationId)
        : subAccounts,
    [subAccounts, selectedOrganizationId],
  );

  const filteredSubAccounts = useMemo(() => {
    return accountSubAccounts.filter((subAccount) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const name = `${subAccount.firstName} ${subAccount.lastName}`.toLowerCase();
        if (!name.includes(query) && !subAccount.email.toLowerCase().includes(query)) {
          return false;
        }
      }

      // Status filter
      if (statusFilter === 'active' && !subAccount.isActive) return false;
      if (statusFilter === 'inactive' && subAccount.isActive) return false;

      return true;
    });
  }, [accountSubAccounts, searchQuery, statusFilter]);

  // ==================== STATS ====================

  const stats = useMemo(() => {
    const total = accountSubAccounts.length;
    const active = accountSubAccounts.filter((s) => s.isActive).length;
    const inactive = total - active;
    const neverSignedIn = accountSubAccounts.filter((s) => !s.lastLoginAt).length;

    return { total, active, inactive, neverSignedIn };
  }, [accountSubAccounts]);

  // ==================== HANDLERS ====================

  const handleOpenModal = (mode: SubAccountFormMode, subAccount?: SubAccount) => {
    setSelectedSubAccount(subAccount || null);
    setFormMode(mode);
  };

  const handleCloseModal = () => {
    setFormMode(null);
    setSelectedSubAccount(null);
  };

  const handleSaved = (message: string) => {
    handleCloseModal();
    showSuccess(message);
    fetchSubAccounts();
  };

  // ==================== RENDER ====================

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-3">
            <UsersRound className="w-8 h-8 text-primary-400" />
            Sub-accounts
          </h1>
          <p className="text-sm text-(--color-muted-gray) mt-1">
            Give team members or branches access to assigned contacts and WhatsApp chat under your plan.
          </p>
        </div>
        <Button onClick={() => handleOpenModal('create')} className="shrink-0">
          <Plus className="w-4 h-4 mr-2" />
          Add Sub-account
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-linear-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center">
              <UsersRound className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{stats.total}</p>
              <p className="text-xs text-(--color-muted-gray)">Total Sub-accounts</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-linear-to-br from-green-500/20 to-green-600/10 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{stats.active}</p>
              <p className="text-xs text-(--color-muted-gray)">Active</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-linear-to-br from-yellow-500/20 to-yellow-600/10 flex items-center justify-center">
              <UserX className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{stats.inactive}</p>
              <p className="text-xs text-(--color-muted-gray)">Inactive</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-linear-to-br from-blue-500/20 to-blue-600/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{stats.neverSignedIn}</p>
              <p className="text-xs text-(--color-muted-gray)">Never Signed In</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--color-muted-gray)" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="w-48">
            <Select
              options={FILTER_OPTIONS}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value as string)}
              placeholder="Filter by status"
            />
          </div>
        </div>
      </Card>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30"
          >
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-300 flex-1">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30"
          >
            <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
            <p className="text-sm text-green-300 flex-1">{success}</p>
            <button onClick={() => setSuccess(null)} className="text-green-400 hover:text-green-300">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading State */}
      {loading ? (
        <Card className="p-8">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
            <span className="text-(--color-muted-gray)">Loading sub-accounts...</span>
          </div>
        </Card>
      ) : filteredSubAccounts.length === 0 ? (
        /* Empty State */
        <Card className="p-8">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center mx-auto mb-4">
              <UsersRound className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">
              {accountSubAccounts.length === 0 ? 'No Sub-accounts Yet' : 'No Sub-accounts Found'}
            </h3>
            <p className="text-sm text-(--color-muted-gray) mb-6 max-w-md mx-auto">
              {accountSubAccounts.length === 0
                ? 'Create sub-accounts for team members or branches. Each can view assigned contacts and chat with them on WhatsApp.'
                : 'No sub-accounts match your current filters. Try adjusting your search or filter criteria.'}
            </p>
            {accountSubAccounts.length === 0 && (
              <Button onClick={() => handleOpenModal('create')}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Sub-account
              </Button>
            )}
          </div>
        </Card>
      ) : (
        /* Sub-accounts List */
        <Card className="overflow-hidden">
          <div className="divide-y divide-(--color-border-gray)">
            {filteredSubAccounts.map((subAccount) => (
              <SubAccountItem
                key={subAccount.id}
                subAccount={subAccount}
                accountName={
                  isMultiOrganization && !selectedOrganizationId
                    ? organizations.find(account => account.id === subAccount.assignedOrganizationId)?.businessName
                    : undefined
                }
                onEdit={() => handleOpenModal('edit', subAccount)}
                onSetPassword={() => handleOpenModal('password', subAccount)}
                onToggleStatus={() => setToggleConfirm(subAccount)}
                onDelete={() => setDeleteConfirm(subAccount)}
                onManageContacts={() => setContactsSubAccount(subAccount)}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Add / Edit / Set Password Modal */}
      <Modal
        isOpen={formMode !== null}
        onClose={handleCloseModal}
        title={formMode ? FORM_TITLES[formMode].title : ''}
        subtitle={
          formMode && selectedSubAccount
            ? `${selectedSubAccount.firstName} ${selectedSubAccount.lastName} — ${selectedSubAccount.email}`
            : formMode
              ? FORM_TITLES[formMode].subtitle
              : ''
        }
        size="lg"
        showCloseButton
      >
        {formMode && (
          <SubAccountForm
            key={`${formMode}-${selectedSubAccount?.id || 'new'}`}
            mode={formMode}
            accounts={isMultiOrganization ? organizations : undefined}
            defaultOrganizationId={selectedOrganizationId ?? organizations.find(account => account.isPrimary)?.id}
            subAccount={selectedSubAccount}
            api={subAccountsService}
            onCancel={handleCloseModal}
            onSaved={handleSaved}
          />
        )}
      </Modal>

      {/* Assigned Contacts Modal */}
      <SubAccountContactsModal
        isOpen={!!contactsSubAccount}
        onClose={() => setContactsSubAccount(null)}
        subAccount={contactsSubAccount}
        onContactsChanged={fetchSubAccounts}
      />

      <SubAccountConfirmDialogs
        deleteConfirm={deleteConfirm}
        onCloseDelete={() => setDeleteConfirm(null)}
        onConfirmDelete={handleDelete}
        deleting={deleting}
        toggleConfirm={toggleConfirm}
        onCloseToggle={() => setToggleConfirm(null)}
        onConfirmToggle={confirmToggleStatus}
        toggling={toggling}
      />
    </div>
  );
};

export default SubAccountsPage;
